/**
 * useAiSecurityNotifications — polls High/Critical AI security notifications
 * and reconciles them into the bell. Producer only: `addMessage`/
 * `updateMessage`/`removeMessage`, matched on `Message.sourceId`.
 */

import { useEffect, useRef } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { useAuth } from '../contexts/AuthContext'
import { useMessaging } from '../contexts/MessagingContext'
import { useTenant } from '../contexts/TenantContext'
import {
  type ApiAiSecurityNotification,
  type ApiAiSecurityNotificationsResponse,
  fetchAiSecurityNotifications,
} from '../services/api/aiSecurityNotifications'
import type {
  Message,
  MessageType,
  MessagingContextValue,
} from '../types/messaging'
import {
  aiIdentityDeepLink,
  aiIdentityRiskListDeepLink,
  shadowAiDeepLink,
} from '../utils/ai/deeplinks'
import { hasAiSecurityReadRole } from '../utils/authUtils'

/** Poll cadence. Matches the producer's own cadence on the backend. */
export const AI_SECURITY_NOTIFICATIONS_POLL_MS = 60_000

/** Page size requested per poll; must be inside the server's 1..200 range. */
const AI_SECURITY_NOTIFICATIONS_LIMIT = 100

/** Tenant-scoped so a tenant switch cannot serve tenant A's rows into tenant
 *  B's bell. */
const aiSecurityNotificationsQueryKey = (
  tenantId: string | undefined,
): readonly unknown[] => ['ai-security-notifications', tenantId]

/** `critical` → `error`, `high` → `warning`. Widening the `severity` union
 *  without adding a case here is a compile error; a wire value the union
 *  doesn't cover (a backend that ships ahead of this file) still falls
 *  through to the amber `'warning'` default rather than reaching the caller
 *  as an invalid `MessageType`. */
const severityToMessageType = (
  severity: ApiAiSecurityNotification['severity'],
): MessageType => {
  switch (severity) {
    case 'critical':
      return 'error'
    case 'high':
      return 'warning'
    default: {
      // Compile-time: a new union member fails to typecheck here.
      // Runtime: a wire value outside the union still degrades safely.
      const _exhaustive: never = severity
      return 'warning'
    }
  }
}

/** Maps a row's route-free `link_descriptor` to the URL the frontend owns the
 *  shape of — the backends store facts, not routes. Falls back to the stored
 *  `deep_link_path` for concern rows and legacy identity rows, and to
 *  non-navigable (`undefined`) when that is empty, so an empty string never
 *  reads as a live link. Exported PURE so the descriptor→URL mapping is
 *  unit-testable without mounting the hook. */
export const resolveDeepLinkPath = (
  row: ApiAiSecurityNotification,
): string | undefined => {
  switch (row.link_descriptor?.kind) {
    case 'ai_identity': {
      const { identity_id, identity_name } = row.link_descriptor
      return aiIdentityDeepLink(identity_id, identity_name)
    }
    case 'ai_identity_risk_list':
      return aiIdentityRiskListDeepLink(row.link_descriptor.risk_levels)
    case 'ai_shadow_ai':
      return shadowAiDeepLink(row.link_descriptor.destination_domain)
    default:
      return row.deep_link_path || undefined
  }
}

/** The `Message` fields a server row owns on every reconcile pass.
 *  Deliberately excludes `id` (assigned by the bell on insert, never by the
 *  server), `target` (set once, on insert, and not part of the server row),
 *  `sourceId` (immutable identity, set once, on insert), and `timestamp`
 *  (derived from `created_at` by the caller, which needs it before and
 *  after this call). */
const messageFieldsFor = (
  row: ApiAiSecurityNotification,
  // Resolved once per row by the caller and threaded in, so the descriptor→URL
  // mapping is not recomputed for the same row within one reconcile pass.
  deepLinkPath: string | undefined,
): Omit<Message, 'id' | 'target' | 'sourceId' | 'timestamp'> => ({
  title: row.title,
  message: row.body,
  type: severityToMessageType(row.severity),
  deepLinkPath,
  // Correct verbatim on an insert; on a patch the reconcile overrides it so a
  // not-yet-round-tripped local mark-read is never reverted.
  read: row.read_at !== null,
})

/**
 * Fold server rows into the bell. Idempotence is load-bearing: an unchanged
 * row (including in the removal pass) must cause no state update, or writes
 * loop. Exported PURE — all three write paths are parameters, for testability.
 */
export const reconcileRowsIntoBell = (
  rows: readonly ApiAiSecurityNotification[],
  bellMessages: readonly Message[],
  /** Rows dismissed since the previous pass; without this they look "absent"
   *  and get re-added — or, post-removal-pass, get spuriously removed here
   *  when the dismiss flow already removed them locally. */
  recentlyDismissed: ReadonlySet<number>,
  addMessage: MessagingContextValue['addMessage'],
  updateMessage: MessagingContextValue['updateMessage'],
  /** B6: drops a bell row the server has stopped returning (dismissed in
   *  another tab, or aged out), so it clears everywhere instead of lingering.
   *  Only called for server-backed rows (`sourceId !== undefined`). */
  removeMessage: MessagingContextValue['removeMessage'],
  /** B6 pagination-safety: on a PARTIAL page, a row absent from the payload
   *  may just be paginated out (still live), not dismissed/aged — the removal
   *  pass uses this to never delete a row older than the page's window. */
  hasMore: boolean,
): void => {
  const bySourceId = new Map<number, Message>()
  for (const msg of bellMessages) {
    if (msg.sourceId !== undefined && !bySourceId.has(msg.sourceId)) {
      bySourceId.set(msg.sourceId, msg)
    }
  }

  // Rows added during this pass are not yet in the snapshot, so a duplicated id
  // inside one payload would otherwise double-insert.
  const handled = new Set<number>()

  for (const row of rows) {
    if (handled.has(row.id)) continue
    handled.add(row.id)

    // Suppressed for exactly this pass: the dismiss POST is un-awaited, so a
    // GET issued before it still carries the row. If the POST actually failed
    // the row returns on the poll after this one, rather than hiding forever.
    if (recentlyDismissed.has(row.id)) continue

    const createdAt = new Date(row.created_at)
    // Resolve the descriptor→URL once per row; both the pathChanged check and
    // messageFieldsFor below reuse it.
    const resolvedPath = resolveDeepLinkPath(row)
    const existing = bySourceId.get(row.id)

    if (!existing) {
      addMessage({
        ...messageFieldsFor(row, resolvedPath),
        target: 'bell',
        sourceId: row.id,
        // Backend clock, so every later comparison is backend-to-backend and
        // the panel sorts on when the event happened.
        timestamp: createdAt,
      })
      continue
    }

    // Nothing to patch without a local id.
    if (!existing.id) continue

    // Compare Date INSTANTS, never RFC3339 strings — fractional seconds and
    // timezone offsets make string ordering wrong.
    const existingAt = existing.timestamp ? existing.timestamp.getTime() : 0
    const isNewer = createdAt.getTime() > existingAt
    // The deep link is re-resolved from live triage state (concern rows) or
    // from the descriptor (identity rows) on every read, so a Confirm — or an
    // identity rename — flips the path while `created_at` stays put. Compare
    // the RESOLVED path, not the raw `deep_link_path`: an identity row's
    // `deep_link_path` is permanently `''`, so comparing it directly would
    // read as "changed" on every single poll and break the idempotence this
    // function depends on.
    const pathChanged = resolvedPath !== existing.deepLinkPath
    // Read state converges in ONE direction only: unread → read. The promotion
    // is required — the server can flip read state with neither `created_at` nor
    // the path moving (the row was read in another tab), and nothing else would
    // fire. The demotion is a bug: `read_at: null` beside a local `read: true`
    // is a mark-read POST that has not round-tripped, and the one LEGITIMATE
    // demotion — a detector re-emit — moves `created_at` forward and so arrives
    // via `isNewer` instead. `=== true` normalises rows rehydrated from an older
    // cache.
    const serverRead = row.read_at !== null
    const locallyRead = existing.read === true
    const readPromoted = serverRead && !locallyRead

    if (!isNewer && !pathChanged && !readPromoted) continue

    updateMessage(existing.id, {
      ...messageFieldsFor(row, resolvedPath),
      // Same asymmetry in the patch itself: a path-only or read-only update
      // must not carry a stale `read: false` in with it.
      read: isNewer ? serverRead : serverRead || locallyRead,
      timestamp: createdAt,
    })
  }

  // Removal pass (B6): the add/patch loop above only touches rows the payload
  // still contains, so a row the server stopped returning (dismissed elsewhere,
  // or aged out) would otherwise live in the bell forever.
  const payloadIds = new Set(rows.map(r => r.id))
  // Pagination boundary: on a PARTIAL page, only remove a row inside the page's
  // visible window (>= its oldest row) — anything older may simply be paginated
  // out, not dismissed. `-Infinity` (COMPLETE page) makes every row in-window.
  let pageOldest = Number.NEGATIVE_INFINITY
  if (hasMore) {
    pageOldest = Number.POSITIVE_INFINITY
    for (const r of rows) {
      const at = new Date(r.created_at).getTime()
      if (at < pageOldest) pageOldest = at
    }
  }
  for (const msg of bellMessages) {
    // Locally-produced messages have no server feed to be "absent" from.
    if (msg.sourceId === undefined) continue
    if (payloadIds.has(msg.sourceId)) continue
    // Suppressed for exactly this pass, same as the add/patch loop above: the
    // dismiss flow already removed this row locally, so removing it again here
    // would be redundant at best and a race at worst.
    if (recentlyDismissed.has(msg.sourceId)) continue
    if (!msg.id) continue
    // Partial page: keep any row older than the page's oldest row — it may just
    // be paginated out, not dismissed. (No-op on a complete page: pageOldest is
    // -Infinity, so this never fires.)
    const msgAt = msg.timestamp ? msg.timestamp.getTime() : 0
    if (msgAt < pageOldest) continue
    removeMessage(msg.id)
  }
}

type AiSecurityNotificationsQuery =
  UseQueryResult<ApiAiSecurityNotificationsResponse>

/** Mount once, inside `MessagingProvider`. The query result is returned for
 *  callers that want fetch state; the standard mount ignores it. */
export const useAiSecurityNotifications = (): AiSecurityNotificationsQuery => {
  const { selectedTenant } = useTenant()
  const { isAuthenticated, user } = useAuth()
  const {
    bellMessages,
    addMessage,
    updateMessage,
    removeMessage,
    reconcileDismissedSourceIds,
  } = useMessaging()

  const tenantId = selectedTenant?.id

  const query = useQuery({
    queryKey: aiSecurityNotificationsQueryKey(tenantId),
    queryFn: async ({
      signal,
    }): Promise<ApiAiSecurityNotificationsResponse> => {
      if (!tenantId) {
        throw new Error('No tenant selected')
      }
      // No `since`. The server floors every request at
      // `max(since - 2min, now - 7d)`, so a browser-clock value can only NARROW
      // the 7-day window — its entire possible effect is to drop notifications
      // on a clock running fast.
      return fetchAiSecurityNotifications(tenantId, {
        limit: AI_SECURITY_NOTIFICATIONS_LIMIT,
        signal,
      })
    },
    // The role gate is required, not defensive: without it every user outside
    // the allowlist fires two failing requests a minute forever (the app
    // QueryClient sets retry: 1).
    enabled:
      !!tenantId && isAuthenticated && hasAiSecurityReadRole(user, tenantId),
    refetchInterval: AI_SECURITY_NOTIFICATIONS_POLL_MS,
    // One poll is the freshness contract, so a second mount or a remount inside
    // the same window does not fire an extra request.
    staleTime: AI_SECURITY_NOTIFICATIONS_POLL_MS,
    // B10: a backgrounded tab (browsers throttle hidden-tab timers) can be
    // stale by more than one poll interval by the time it's refocused; refetch
    // on focus reconverges immediately. `staleTime` above still bounds the load.
    refetchOnWindowFocus: true,
  })

  // The reconcile needs the CURRENT bell contents but must not re-run when the
  // bell changes: `bellMessages` is a fresh array on every provider render, so
  // depending on it directly would re-enter the reconcile after its own writes.
  const bellRef = useRef<readonly Message[]>(bellMessages)
  useEffect(() => {
    bellRef.current = bellMessages
  }, [bellMessages])

  // `dataUpdatedAt` is in the deps on purpose: it runs once per SUCCESSFUL POLL,
  // not once per changed payload — `structuralSharing` returns the SAME `data`
  // for a deeply-equal poll, which would otherwise silently disable the suppression's PRUNE half. Safe because the reconcile is strictly idempotent.
  const { data, dataUpdatedAt } = query
  useEffect(() => {
    // `dataUpdatedAt` is 0 until the first successful fetch.
    if (!data || dataUpdatedAt === 0) return
    const rows = data.notifications
    // Sticky suppression, pruned by server confirmation (B11): stays suppressed
    // while the server still returns the id, pruned once it doesn't. Runs on
    // every poll, including an empty one, so the removal pass (B6) still runs.
    const presentIds = new Set(rows.map(row => row.id))
    const recentlyDismissed = reconcileDismissedSourceIds(presentIds)
    reconcileRowsIntoBell(
      rows,
      bellRef.current,
      recentlyDismissed,
      addMessage,
      updateMessage,
      removeMessage,
      // B6 pagination-safety: gates the removal pass so rows beyond the newest
      // page (this feed is capped at AI_SECURITY_NOTIFICATIONS_LIMIT) are not
      // deleted as if dismissed. `?? false` matches the API client's own guard.
      data.has_more ?? false,
    )
  }, [
    data,
    dataUpdatedAt,
    addMessage,
    updateMessage,
    removeMessage,
    reconcileDismissedSourceIds,
  ])

  return query
}
